// src/components/ui/WhatsNewBanner.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Existing users created before this date see the banner.
// New signups after this date never see it.
const RELEASE_CUTOFF = '2026-03-16T00:00:00Z';
const DISMISS_KEY = 'sh_whats_new_dismissed_v1';

export function WhatsNewBanner() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(true); // default hidden until we check

  useEffect(() => {
    if (!profile) return;

    // Only show to existing users (created before the release cutoff)
    const createdAt = profile.created_at_profile;
    if (!createdAt) {
      setDismissed(true);
      return;
    }

    const userCreatedDate = new Date(createdAt);
    const cutoff = new Date(RELEASE_CUTOFF);

    if (userCreatedDate >= cutoff) {
      // New user — never show
      setDismissed(true);
      return;
    }

    // Existing user — check localStorage
    const wasDismissed = localStorage.getItem(DISMISS_KEY) === 'true';
    setDismissed(wasDismissed);
  }, [profile]);

  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  return (
    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 shrink-0">
      <div className="flex items-center gap-2.5 min-w-0">
        <Info className="h-4 w-4 shrink-0 text-white" />
        <p className="text-sm font-medium truncate">
          <span className="font-semibold">New features just dropped!</span>
          {' '}CRM, Workflows, Booking Pages & more are live.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => navigate('/dashboard/whats-new')}
          className="text-xs font-semibold bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-md px-3 py-1.5 transition-colors whitespace-nowrap"
        >
          See What&apos;s New
        </button>
        <button
          onClick={handleDismiss}
          className="p-1 rounded hover:bg-white/20 transition-colors"
          aria-label="Dismiss banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
